import { Grid, IconButton, Paper, Tooltip } from "@material-ui/core";
import React, { Suspense, useCallback, useEffect, useState } from "react";
import { dateTimeZoneConverter } from "../../../utils/commonFunctions/date";
import { downloadFileFromMongoDB } from "../../../utils/commonFunctions/downloadFileFromDatabase";
import generateImageUrl from "../../../utils/commonFunctions/imageUrl";
import { LazyCustomImageDialogWithTitle, LazyCustomPdfViewWithTitleDialog } from "..";
import { generateFileURL } from "../../../utils/commonFunctions/generateFileURL";
import { MulterGridFSFile } from "../../../@types/files.types";
import { DialogStaticProps } from "../../../@types/components/dialogProps.types";

import VisibilityIcon from "@material-ui/icons/Visibility";
import VisibilityOffIcon from "@material-ui/icons/VisibilityOff";
import GetAppRoundedIcon from "@material-ui/icons/GetAppRounded";
import ErrorIcon from "@material-ui/icons/Error";
import Coverage from "../../pages/employer/plan/cr/coverage/Coverage";
import CoverageGrid from "./coverageGrid/CoverageGrid";
import { Button, Checkbox, Chip, FormControlLabel, TextField } from "@mui/material";
import shortenFileName from "../../../utils/commonFunctions/shortenFileName";
import { CheckOutlined } from "@material-ui/icons";
import { trackPromise } from "react-promise-tracker";
import { api } from "../../../utils/api";

import { useNavigate } from "react-router-dom";
import useRouteParam from "../../../hooks/useRouteParam";
import { active } from "d3";

// const premiumCoverageColumns = [
// 	"Coverage",
// 	"Active",
// 	"Premium Type"
// 	// "Monthly Premium",
// 	// "Monthly Max Premium",
// 	// "Monthly Min Premium",
// 	// "Weekly  Premium",
// 	// "Weekly Max Premium",
// 	// "Weekly Min Premium"
// ];
// const employerContibutionColumns = [
// 	"Coverage",
// 	"Active"
// 	// "Weekly", "Monthly"
// ];

const DialogStaticForm = ({ planDetails }: DialogStaticProps) => {
	// console.log("this is the plan details", planDetails);
	const [eventName, setEventName] = useState("");
	const [pdfProps, setPdfProps] = useState("");
	const [viewableDocument, setViewableDocument] = useState<boolean | null>(null);

	const queryGroupNumber = useRouteParam("gn");

	const [vebPlanDocument, setVEBPlanDocument] = useState<any>(null);
	const [imageDialogProps, setImageDialogProps] = useState({
		openDialog: false,
		image: undefined
	});
	const [openPdfViewDialogProps, setOpenPdfViewDialogProps] = useState({
		openDialog: false
	});

	const [employerContributionColumns, setEmployerContributionColumns] = useState<any>([]);
	const [employeeCoverageColumns, setEmployeeCoverageColumns] = useState<any>([]);

	const handlePdfViewDialogClose = useCallback(() => {
		setOpenPdfViewDialogProps(Object.assign({}, openPdfViewDialogProps, { openDialog: false }));
	}, [openPdfViewDialogProps]);

	const handlePdfViewDialogOpen = useCallback(() => {
		setOpenPdfViewDialogProps(Object.assign({}, openPdfViewDialogProps, { openDialog: true }));
	}, [openPdfViewDialogProps]);

	const navigate = useNavigate();

	const handleImageDialogClose = useCallback(() => {
		setImageDialogProps(
			Object.assign({}, imageDialogProps, {
				openDialog: false
			})
		);
	}, [imageDialogProps]);

	const handleViewDocumentClick = (file: any) => {
		// console.log("File Info => ", file);
		if (file.mimetype === "image/png" || file.mimetype === "image/jpg" || file.mimetype === "image/jpeg") {
			const imageInstance = file;

			setImageDialogProps(
				Object.assign({}, imageDialogProps, {
					openDialog: true,
					image: generateImageUrl(imageInstance)
				})
			);
		}

		if (file.mimetype === "application/pdf") handlePdfViewDialogOpen();
	};

	const handleDocumentChange = useCallback(
		(event: React.ChangeEvent<HTMLInputElement>) => {
			const { files } = event.target;
			// console.log(files);
			if (files) {
				setVEBPlanDocument(Object.assign({}, vebPlanDocument, { document: files[0] }));
				event.target.value = "";
			}
		},
		[vebPlanDocument]
	);

	const handleDeleteDocument = useCallback(() => {
		setVEBPlanDocument(Object.assign({}, vebPlanDocument, { document: null }));
	}, [vebPlanDocument]);

	const handleDocumentUpdate = useCallback(
		async (e: any) => {
			e.stopPropagation();

			const formData: FormData = new FormData();

			if (planDetails && planDetails._id) {
				// formData.append("_id", planDetails._id);
				formData.append(
					"plan",
					JSON.stringify({ _id: planDetails._id, group_number: queryGroupNumber as number, document: null })
				);
				// console.log(planDetails);
				formData.append("document", vebPlanDocument.document as unknown as File);
				const updatedDocument = await trackPromise(api.plan.updatePlanDocument(formData));
				if (updatedDocument) {
					alert("Document is succesfully updated");

					// navigate(pathname);
					window.location.reload();
				}
				setVEBPlanDocument(Object.assign({}, vebPlanDocument, { document: null }));
			} else {
				alert("no plan id detected");
			}
		},
		[planDetails, vebPlanDocument, queryGroupNumber]
	);

	const handlePDFViewClick = useCallback(() => {
		if (planDetails?.document === null) {
			return;
		}

		const document = planDetails.document as MulterGridFSFile;
		if (document && document.mimetype === "application/pdf") {
			const pdfURL = generateFileURL(planDetails?.document);
			setPdfProps(pdfURL);
		} else {
			// console.log("Not PDF");
		}

		if (document && document.originalname) setEventName(document.originalname);
	}, [planDetails]);

	const viewableDocumentsHandler = useCallback(() => {
		if (planDetails?.document === null) return;

		const document = planDetails.document as MulterGridFSFile;

		if (
			document &&
			(document.mimetype === "application/pdf" ||
				document.mimetype === "image/png" ||
				document.mimetype === "image/jpg" ||
				document.mimetype === "image/jpeg")
		) {
			setViewableDocument(true);
		}
	}, [planDetails.document]);

	const transformCoverageDynamic = useCallback((coverage: any, type = "employee") => {
		const result: any = {};

		// console.log("coverage", coverage);
		Object.entries(coverage).forEach(([key, cov]: [string, any]) => {
			const transformed: any = {};

			// Handle boolean values (e.g. premium_without_dependent)
			if (typeof cov === "boolean") {
				transformed.premium_type = null;
				transformed.active = cov; // true or false from backend

				result[key] = transformed;
				return;
			}

			// For each premium entry, dynamically create keys

			cov.premiums?.forEach((p: any) => {
				if (!p?.pay_frequency) return;

				const freq = p.pay_frequency.toLowerCase();

				transformed[`${freq}_premium`] = p.value ?? null;
				// console.log(p.value);
				// "Monthly Premium",
				// "Monthly Max Premium",
				// "Monthly Min Premium",
				// "Weekly  Premium",
				// "Weekly Max Premium",
				// "Weekly Min Premium"

				if (type === "employee") {
					transformed[`${freq}_minimum_premium`] = p.minimum ?? null;
					transformed[`${freq}_maximum_premium`] = p.maximum ?? null;
				}
			});

			if (type === "employee") {
				// Copy other fields
				transformed.premium_type = cov.premium_type ?? null;
			}
			transformed.active = cov.active ?? false;

			result[key] = transformed;
		});

		return result;
	}, []);

	const createColumnsForCoverage = useCallback(() => {
		const coverage = planDetails.coverage;

		if (!coverage) return;
		const employerContributionColumns = ["Coverage", "Active"];
		const employeeContributionColumns = ["Coverage", "Active", "Premium Type"];
		let done = false;

		Object.entries(coverage).forEach(([key, cov]: [string, any]) => {
			if (!done) {
				cov.premiums?.forEach((p: any) => {
					if (!p?.pay_frequency) return;

					employerContributionColumns.push(
						p.pay_frequency.charAt(0).toUpperCase() + p.pay_frequency.slice(1).toLowerCase()
					);

					employeeContributionColumns.push(
						p.pay_frequency.charAt(0).toUpperCase() + p.pay_frequency.slice(1).toLowerCase() + " Premium"
					);

					employeeContributionColumns.push(
						p.pay_frequency.charAt(0).toUpperCase() +
							p.pay_frequency.slice(1).toLowerCase() +
							" Max Premium"
					);
					employeeContributionColumns.push(
						p.pay_frequency.charAt(0).toUpperCase() +
							p.pay_frequency.slice(1).toLowerCase() +
							" Min Premium"
					);
				});
				done = true;
				setEmployeeCoverageColumns(employeeContributionColumns);
				setEmployerContributionColumns(employerContributionColumns);
			}
		});
	}, [planDetails]);

	// Example

	useEffect(() => {
		createColumnsForCoverage();
	}, [planDetails?.coverage]);

	useEffect(() => {
		handlePDFViewClick();
		viewableDocumentsHandler();
	}, [handlePDFViewClick, viewableDocumentsHandler]);
	// console.log("plandetails________________:", planDetails);

	return (
		<div>
			<Suspense fallback={<div />}>
				<LazyCustomImageDialogWithTitle
					dialogProps={imageDialogProps}
					handleImageDialogClose={handleImageDialogClose}
					eventName={eventName}
					eventDate={null}
				/>
			</Suspense>
			<Suspense>
				<LazyCustomPdfViewWithTitleDialog
					dialogProps={openPdfViewDialogProps}
					handleClose={handlePdfViewDialogClose}
					pdfFile={pdfProps}
					eventName={eventName}
					eventDate={null}
				/>
			</Suspense>

			<div className="dialog-static-form">
				<Grid container spacing={1} className=" grid-container" style={{ border: "none" }}>
					<Paper className="pf-paper-container static-plan-container" elevation={3} variant="outlined">
						<Grid item xl={12} lg={12} md={12} sm={12} xs={12}>
							<Grid container spacing={1} className="pf-label">
								<Grid item xs={12} sm={12} md={3} lg={3} xl={3}>
									<div className="pf-label-text">Plan Name</div>
								</Grid>
								<Grid item xs={9} sm={9} md={9} lg={9} xl={9}>
									<div className="field-value">{planDetails?.plan_name}</div>
								</Grid>
							</Grid>
						</Grid>
						<Grid item xl={12} lg={12} md={12} sm={12} xs={12}>
							<Grid container spacing={1} className="pf-label">
								<Grid item xs={12} sm={12} md={3} lg={3} xl={3}>
									<div className="pf-label-text">Plan Code</div>
								</Grid>
								<Grid item xs={9} sm={9} md={9} lg={9} xl={9}>
									<div className="field-value">{planDetails?.plan_code}</div>
								</Grid>
							</Grid>
						</Grid>
						<Grid item xl={12} lg={12} md={12} sm={12} xs={12}>
							<Grid container spacing={1} className="pf-label">
								<Grid item xs={12} sm={12} md={3} lg={3} xl={3}>
									<div className="pf-label-text">Group Number</div>
								</Grid>
								<Grid item xs={9} sm={9} md={9} lg={9} xl={9}>
									<div className="field-value">
										{planDetails?.group_number ? planDetails?.group_number : "N/A"}
									</div>
								</Grid>
							</Grid>
						</Grid>
						<Grid item xl={12} lg={12} md={12} sm={12} xs={12}>
							<Grid container spacing={1} className="pf-label">
								<Grid item xs={12} sm={12} md={3} lg={3} xl={3}>
									<div className="pf-label-text">Employee Class</div>
								</Grid>
								<Grid item xs={9} sm={9} md={9} lg={9} xl={9}>
									<div className="field-value">
										{planDetails?.employee_class ? planDetails?.employee_class : "N/A"}
									</div>
								</Grid>
							</Grid>
						</Grid>
						<Grid item xl={12} lg={12} md={12} sm={12} xs={12}>
							<Grid container spacing={1} className="pf-label">
								<Grid item xs={12} sm={12} md={3} lg={3} xl={3}>
									<div className="pf-label-text">Auto Approve</div>
								</Grid>
								<Grid item xs={9} sm={9} md={9} lg={9} xl={9}>
									<div className="field-value">
										{planDetails?.auto_approve === false ? "No" : "Yes"}
									</div>
								</Grid>
							</Grid>
						</Grid>
						<Grid item xl={12} lg={12} md={12} sm={12} xs={12}>
							<Grid container spacing={1} className="pf-label">
								<Grid item xs={12} sm={12} md={3} lg={3} xl={3}>
									<div className="pf-label-text">Buy Up Eligibiliy</div>
								</Grid>
								<Grid item xs={9} sm={9} md={9} lg={9} xl={9}>
									<div className="field-value">
										{/* {planDetails?.buy_up_eligibility ? planDetails?.buy_up_eligibility : "N/A"} */}
										{(planDetails?.buy_up_eligibility as unknown as string) === "true"
											? "Yes"
											: (planDetails?.buy_up_eligibility as unknown as string) === "false"
											? "No"
											: "N/A"}
									</div>
								</Grid>
							</Grid>
						</Grid>
						<Grid item xl={12} lg={12} md={12} sm={12} xs={12}>
							<Grid container spacing={1} className="pf-label">
								<Grid item xs={12} sm={12} md={3} lg={3} xl={3}>
									<div className="pf-label-text">Plan Start Date</div>
								</Grid>
								<Grid item xs={9} sm={9} md={9} lg={9} xl={9}>
									<div className="field-value">
										{planDetails?.start_date
											? dateTimeZoneConverter(planDetails?.start_date).toString()
											: "N/A"}
									</div>
								</Grid>
							</Grid>
						</Grid>
						<Grid item xl={12} lg={12} md={12} sm={12} xs={12}>
							<Grid container spacing={1} className="pf-label">
								<Grid item xs={12} sm={12} md={3} lg={3} xl={3}>
									<div className="pf-label-text">Plan End Date</div>
								</Grid>
								<Grid item xs={9} sm={9} md={9} lg={9} xl={9}>
									<div className="field-value">
										{planDetails?.end_date
											? dateTimeZoneConverter(planDetails?.end_date).toString()
											: "N/A"}
									</div>
								</Grid>
							</Grid>
						</Grid>
						<Grid item xl={12} lg={12} md={12} sm={12} xs={12}>
							<Grid container spacing={1} className="pf-label">
								<Grid item xs={12} sm={12} md={3} lg={3} xl={3}>
									<div className="pf-label-text">Plan Appearance Order</div>
								</Grid>
								<Grid item xs={9} sm={9} md={9} lg={9} xl={9}>
									<div className="field-value">
										{planDetails?.plan_order ? planDetails?.plan_order.toString() : "N/A"}
									</div>
								</Grid>
							</Grid>
						</Grid>
						<Grid item xl={12} lg={12} md={12} sm={12} xs={12}>
							<Grid container spacing={1} className="pf-label">
								<Grid item xs={12} sm={12} md={3} lg={3} xl={3}>
									<div className="pf-label-text">Is this plan Always Open?</div>
								</Grid>
								<Grid item xs={9} sm={9} md={9} lg={9} xl={9}>
									<div className="field-value">{planDetails?.is_always_open ? "Yes" : "No"}</div>
								</Grid>
							</Grid>
						</Grid>
						<Grid item xl={12} lg={12} md={12} sm={12} xs={12}>
							<Grid container spacing={1} className="pf-label">
								<Grid item xs={12} sm={12} md={3} lg={3} xl={3}>
									<div className="pf-label-text">Does this plan need Beneficiaries?</div>
								</Grid>
								<Grid item xs={9} sm={9} md={9} lg={9} xl={9}>
									<div className="field-value">
										{planDetails?.beneficiaries_required ? "Yes" : "No"}
									</div>
								</Grid>
							</Grid>
						</Grid>
						<Grid item xl={12} lg={12} md={12} sm={12} xs={12}>
							<Grid container spacing={1} className="pf-label">
								<Grid item xs={12} sm={12} md={3} lg={3} xl={3}>
									<div className="pf-label-text">Does this plan need Waive Approval?</div>
								</Grid>
								<Grid item xs={9} sm={9} md={9} lg={9} xl={9}>
									<div className="field-value">{planDetails?.has_waive_approval ? "Yes" : "No"}</div>
								</Grid>
							</Grid>
						</Grid>
						<Grid item xl={12} lg={12} md={12} sm={12} xs={12}>
							<Grid container spacing={1} className="pf-label">
								<Grid item xs={12} sm={12} md={3} lg={3} xl={3}>
									<div className="pf-label-text">Does this plan need Premium?</div>
								</Grid>
								<Grid item xs={9} sm={9} md={9} lg={9} xl={9}>
									<div className="field-value">{planDetails?.has_premium ? "Yes" : "No"}</div>
								</Grid>
							</Grid>
						</Grid>
						<Grid item xl={12} lg={12} md={12} sm={12} xs={12}>
							<Grid container spacing={1} className="pf-label">
								<Grid item xs={12} sm={12} md={3} lg={3} xl={3}>
									<div className="pf-label-text">
										Does this plan need Employer&apos;s Contribution?
									</div>
								</Grid>
								<Grid item xs={9} sm={9} md={9} lg={9} xl={9}>
									<div className="field-value">
										{planDetails?.has_employer_contribution ? "Yes" : "No"}
									</div>
								</Grid>
							</Grid>
						</Grid>
						<Grid item xl={12} lg={12} md={12} sm={12} xs={12}>
							<Grid container spacing={1} className="pf-label">
								<Grid item xs={12} sm={12} md={3} lg={3} xl={3}>
									<div className="pf-label-text">Does employee can do any custom contribution?</div>
								</Grid>
								<Grid item xs={9} sm={9} md={9} lg={9} xl={9}>
									<div className="field-value">
										{planDetails?.has_employee_custom_contribution ? "Yes" : "No"}
									</div>
								</Grid>
							</Grid>
						</Grid>

						{planDetails?.has_employee_custom_contribution && (
							<Grid item xl={12} lg={12} md={12} sm={12} xs={12}>
								<Grid container spacing={1} className="pf-label">
									<Grid item xs={12} sm={12} md={3} lg={3} xl={3}>
										<div className="pf-label-text">Custom Contribution of Employee (Min Limit)</div>
									</Grid>
									<Grid item xs={9} sm={9} md={9} lg={9} xl={9}>
										<div className="field-value">
											{planDetails?.has_employee_custom_contribution
												? `$${planDetails?.custom_contribution_of_employees.min_limit}`
												: "N/A"}
										</div>
									</Grid>
								</Grid>
							</Grid>
						)}
						<Grid item xl={12} lg={12} md={12} sm={12} xs={12}>
							<Grid container spacing={1} className="pf-label">
								<Grid item xs={12} sm={12} md={3} lg={3} xl={3}>
									<div className="pf-label-text">Plan Dependencies are:</div>
								</Grid>
								<Grid item xs={9} sm={9} md={9} lg={9} xl={9}>
									<div className="field-value">
										{planDetails?.plan_dependencies?.length > 0 ? "" : "N/A"}
									</div>
								</Grid>
							</Grid>
						</Grid>
						{planDetails?.plan_dependencies?.map((dep: any, index: any) => (
							<Grid container spacing={2} alignItems="center" key={index} style={{ marginTop: "8px" }}>
								<Grid item xs={12} sm={3}>
									<TextField fullWidth label="Plan Name" value={dep.plan_name} disabled />
								</Grid>
								<Grid item xs={12} sm={3}>
									<TextField fullWidth label="Plan Code" value={dep.plan_code} disabled />
								</Grid>
								<Grid item xs={12} sm={3}>
									<FormControlLabel
										control={<Checkbox checked={dep.is_taken_together} disabled />}
										label="Taken Together"
									/>
								</Grid>
							</Grid>
						))}
						<Grid item xl={12} lg={12} md={12} sm={12} xs={12}>
							<Grid container spacing={1} className="pf-label" alignItems="center">
								<Grid item xs={12} sm={12} md={3} lg={3} xl={3}>
									<div className="pf-label-text">Dependent Eligibility</div>
								</Grid>
								<Grid item xs={9} sm={9} md={9} lg={9} xl={9}>
									<div className="field-value">
										{planDetails?.dependent_eligibility?.length > 0 ? "" : "N/A"}
									</div>
								</Grid>
							</Grid>
						</Grid>
						{planDetails?.dependent_eligibility?.map((dep: any, index: any) => (
							<Grid container spacing={2} alignItems="center" key={index}>
								<Grid item xs={12} sm={3}>
									<TextField
										fullWidth
										label="Relationship Type"
										value={dep.relationship_type}
										disabled
									/>
								</Grid>
								<Grid item xs={12} sm={3}>
									<TextField type="number" fullWidth label="Min Age" value={dep.min_age} disabled />
								</Grid>
								<Grid item xs={12} sm={3}>
									<TextField type="number" fullWidth label="Max Age" value={dep.max_age} disabled />
								</Grid>
							</Grid>
						))}
						<Grid item xl={12} lg={12} md={12} sm={12} xs={12}>
							<Grid container spacing={1} className="pf-label" alignItems="center">
								<Grid item xs={12} sm={12} md={3} lg={3} xl={3}>
									<div className="pf-label-text">Internal Dependencies</div>
								</Grid>
								<Grid item xs={9} sm={9} md={9} lg={9} xl={9}>
									<div className="field-value">
										{planDetails?.internal_dependencies?.length > 0 ? "" : "N/A"}
									</div>
								</Grid>
							</Grid>
						</Grid>
						{planDetails?.internal_dependencies?.map((dep: any, index: any) => (
							<Grid container spacing={2} alignItems="center" key={index}>
								<Grid item xs={12} sm={3}>
									<FormControlLabel
										control={<Checkbox checked={dep.sub_plan.required} disabled />}
										label="Required"
									/>
								</Grid>
								<Grid item xs={12} sm={5}>
									<TextField fullWidth label="Sub Plan Name" value={dep.sub_plan.name} disabled />
								</Grid>
							</Grid>
						))}
						<Grid item xl={12} lg={12} md={12} sm={12} xs={12}>
							<Grid container spacing={1} className="pf-label">
								<Grid item xs={12} sm={12} md={3} lg={3} xl={3}>
									<div className="pf-label-text">Status</div>
								</Grid>
								<Grid item xs={9} sm={9} md={9} lg={9} xl={9}>
									<div className="field-value">
										{planDetails?.status.charAt(0).toUpperCase() +
											planDetails?.status.slice(1).toLowerCase()}
									</div>
								</Grid>
							</Grid>
						</Grid>
						<Grid item xl={12} lg={12} md={12} sm={12} xs={12}>
							<Grid container spacing={1} className="pf-label">
								<Grid item xs={12} sm={12} md={3} lg={3} xl={3}>
									<div className="pf-label-text">Documentations</div>
								</Grid>
								<Grid item xs={9} sm={9} md={9} lg={9} xl={9}>
									<div className="field-value view-and-download">
										{planDetails?.document ? (
											<div>
												{viewableDocument ? (
													<Tooltip
														title="View Document"
														enterDelay={500}
														leaveDelay={200}
														arrow
													>
														<IconButton>
															<VisibilityIcon
																onClick={() =>
																	handleViewDocumentClick(planDetails?.document)
																}
															/>
														</IconButton>
													</Tooltip>
												) : (
													<Tooltip
														title="Sorry, you cannot view the document. However, you can download it"
														enterDelay={500}
														leaveDelay={200}
														arrow
													>
														<IconButton>
															<VisibilityOffIcon />
														</IconButton>
													</Tooltip>
												)}
												<Tooltip
													title="Download Document"
													enterDelay={500}
													leaveDelay={200}
													arrow
												>
													<IconButton
														onClick={() => downloadFileFromMongoDB(planDetails?.document)}
													>
														<GetAppRoundedIcon />
													</IconButton>
												</Tooltip>
												{vebPlanDocument?.document === null || !vebPlanDocument ? (
													<>
														<input
															className={"document-upload"}
															id={"document-upload"}
															name={"Document"}
															type="file"
															onChange={handleDocumentChange}
															style={{ display: "none" }}
															accept="image/*, .pdf"
														/>
														<label htmlFor={"document-upload"}>
															<Button
																className="button"
																component="span"
																variant="contained"
																style={{
																	backgroundColor: "#3A4652",
																	color: "#ffff",
																	width: 210
																}}
															>
																<span
																	className="button-label-with-icon"
																	style={{ color: "#ffff" }}
																>
																	{"Edit Document"}
																</span>
																{/* <span>
																	<PublishIcon
																		className="button-icon"
																		style={{ color: "#ffff" }}
																	/>
																</span> */}
															</Button>
														</label>
													</>
												) : vebPlanDocument?.document ? (
													<>
														<Tooltip title={"Click to download"} arrow>
															<Chip
																label={
																	vebPlanDocument
																		? shortenFileName(
																				vebPlanDocument?.document["name"]
																		  )
																		: "No files added"
																}
																onDelete={() => handleDeleteDocument()}
																// onClick={() => downloadFile(plan?.document)}
															/>
														</Tooltip>

														<Button
															className="button"
															component="span"
															variant="contained"
															style={{
																backgroundColor: "#3A4652",
																color: "#ffff",
																width: 210
															}}
															onClick={handleDocumentUpdate}
														>
															<span
																className="button-label-with-icon"
																style={{ color: "#ffff" }}
															>
																{"Update Document"}
															</span>
														</Button>
													</>
												) : null}
											</div>
										) : (
											<>
												<div>
													<span style={{ fontSize: "17px" }}>Not Available</span>
													<Tooltip
														title="Document not provided"
														enterDelay={500}
														leaveDelay={200}
														arrow
													>
														<IconButton>
															<ErrorIcon />
														</IconButton>
													</Tooltip>
													<Suspense fallback={<div />}></Suspense>

													{vebPlanDocument?.document === null || !vebPlanDocument ? (
														<>
															<input
																className={"document-upload"}
																id={"document-upload"}
																name={"Document"}
																type="file"
																onChange={handleDocumentChange}
																style={{ display: "none" }}
																accept="image/*, .pdf"
															/>
															<label htmlFor={"document-upload"}>
																<Button
																	className="button"
																	component="span"
																	variant="contained"
																	style={{
																		backgroundColor: "#3A4652",
																		color: "#ffff",
																		width: 210
																	}}
																>
																	<span
																		className="button-label-with-icon"
																		style={{ color: "#ffff" }}
																	>
																		{"Edit Document"}
																	</span>
																	{/* <span>
																	<PublishIcon
																		className="button-icon"
																		style={{ color: "#ffff" }}
																	/>
																</span> */}
																</Button>
															</label>
														</>
													) : vebPlanDocument?.document ? (
														<>
															<Tooltip title={vebPlanDocument?.document["name"]} arrow>
																<Chip
																	label={
																		vebPlanDocument
																			? shortenFileName(
																					vebPlanDocument?.document["name"]
																			  )
																			: "No files added"
																	}
																	onDelete={() => handleDeleteDocument()}
																	// onClick={() => downloadFile(plan?.document)}
																/>
															</Tooltip>

															<Button
																className="button"
																component="span"
																variant="contained"
																style={{
																	backgroundColor: "#3A4652",
																	color: "#ffff",
																	width: 210
																}}
																onClick={handleDocumentUpdate}
															>
																<span
																	className="button-label-with-icon"
																	style={{ color: "#ffff" }}
																>
																	{"Update Document"}
																</span>
															</Button>
														</>
													) : null}
												</div>
											</>
										)}
									</div>
								</Grid>
							</Grid>
						</Grid>
					</Paper>
					{planDetails?.has_premium && (
						<Paper className="pf-paper-container static-plan-container" elevation={3} variant="outlined">
							<Grid container spacing={1} className="pf-grid-container">
								<div className="pf-title">Premium Details</div>
							</Grid>
							<CoverageGrid
								planDetails={transformCoverageDynamic(planDetails.coverage)}
								coverageType={"employers_contribution"}
								columns={employeeCoverageColumns}
								planName={planDetails?.plan_name}
							/>
						</Paper>
					)}

					{planDetails?.internal_dependencies?.length > 0 &&
						planDetails?.internal_dependencies.map((item: any, index: number) => (
							<Paper
								key={index}
								className="pf-paper-container static-plan-container"
								elevation={3}
								variant="outlined"
							>
								<Grid container spacing={1} className="pf-grid-container">
									<div className="pf-title">Premium Details of sub_plan ({item.sub_plan.name})</div>
								</Grid>
								<CoverageGrid
									planDetails={transformCoverageDynamic(item.sub_plan.coverage)}
									coverageType={"employers_contribution"}
									columns={employeeCoverageColumns}
									planName={planDetails?.plan_name}
								/>
							</Paper>
						))}
					{planDetails.has_employer_contribution === true ? (
						<Paper className="pf-paper-container static-plan-container" elevation={3} variant="outlined">
							<Grid container spacing={1} className="pf-grid-container">
								<div className="pf-title">Employer&apos;s Contribution</div>
							</Grid>
							<CoverageGrid
								planDetails={transformCoverageDynamic(planDetails.employer_contribution, "employer")}
								coverageType={"employers_contribution"}
								columns={employerContributionColumns}
								planName={planDetails?.employer_contribution}
							/>
						</Paper>
					) : null}
				</Grid>
			</div>
		</div>
	);
};

export default DialogStaticForm;
